import React from "react";
import { Link } from "react-router-dom";
import { ROUTES } from "../config/constants";
import Button from "../components/ui/Button";
import Card, { CardBody } from "../components/ui/Card";
import ReviewsSection from "../components/ReviewsSection";
import SEO from "../components/SEO";
import { seoConfig, businessSchema } from "../config/seo.config";
import heroBannerVideo from "../assets/video/WIH Media Hero Banner.compressed.mp4";

const HomePage: React.FC = () => {
  return (
    <div>
      <SEO {...seoConfig.home} structuredData={businessSchema} />
      {/* Hero Section */}
      <section className="relative overflow-hidden py-20 md:py-28 bg-gradient-to-br from-primary-50 via-white to-neutral-50 dark:from-neutral-900 dark:via-neutral-900 dark:to-neutral-800">
        <div className="absolute inset-0 z-0">
          <video
            className="h-full w-full object-cover motion-reduce:hidden"
            autoPlay
            muted
            loop
            playsInline
            preload="none"
            aria-hidden="true"
            webkit-playsinline="true"
          >
            <source src={heroBannerVideo} type="video/mp4" />
          </video>
        </div>
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiMwMDAiIGZpbGwtb3BhY2l0eT0iMC4wMyI+PHBhdGggZD0iTTM2IDM0djItaDJ2LTJoLTJ6bTAtNHYyaDJ2LTJoLTJ6bTAtNHYyaDJ2LTJoLTJ6bTAtNHYyaDJ2LTJoLTJ6bTAtNHYyaDJ2LTJoLTJ6bTAtNHYyaDJ2LTJoLTJ6bTAtNHYyaDJ2LTJoLTJ6bTAtNHYyaDJ2LTJoLTJ6bTAtNHYyaDJ2LTJoLTJ6bS0yIDJ2Mmg0di0yaC00em0tMiAwdjJoMnYtMmgtMnptLTIgMHYyaDJ2LTJoLTJ6bS0yIDB2Mmgydi0yaC0yem0tMiAwdjJoMnYtMmgtMnptLTIgMHYyaDJ2LTJoLTJ6bS0yIDB2Mmgydi0yaC0yem0tMiAwdjJoMnYtMmgtMnptLTIgMHYyaDJ2LTJoLTJ6Ii8+PC9nPjwvZz48L3N2Zz4=')] opacity-20 pointer-events-none"></div>
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-24 -left-24 w-[28rem] h-[28rem] bg-primary-400/25 dark:bg-primary-500/25 rounded-full blur-3xl" />
          <div className="absolute -bottom-32 -right-24 w-[30rem] h-[30rem] bg-primary-600/15 dark:bg-primary-700/20 rounded-full blur-3xl" />
          <div className="absolute inset-0 bg-gradient-to-t from-white/70 via-white/20 to-transparent dark:from-neutral-900/50 dark:via-transparent" />
        </div>

        {/* Bottom gradient for improved hero text contrast */}
        <div className="absolute bottom-0 inset-x-0 h-[45%] bg-gradient-to-t from-white/80 to-transparent dark:from-neutral-900/80 pointer-events-none z-[5]" />

        <div className="section-container relative z-10">
          <div className="max-w-4xl">
            <h1 className="mt-5 text-5xl md:text-6xl lg:text-7xl font-extrabold tracking-tight text-neutral-900 dark:text-white">
              Elevate Your Real Estate Listings in Indian River County
            </h1>

            <div className="mt-8 flex flex-wrap gap-5">
              <Link to={ROUTES.BOOKING}>
                <Button variant="primary" size="lg">
                  Book a Session
                </Button>
              </Link>
              <Link to={ROUTES.PORTFOLIO}>
                <Button variant="outline" size="lg">
                  View Portfolio
                </Button>
              </Link>
            </div>

            <div className="mt-10">
              <p className="text-sm uppercase tracking-widest text-neutral-700 dark:text-neutral-300">
                Trusted by leading agents & brokers
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Popular Packages Strip (from Services) */}
      <section className="py-10 bg-neutral-900 border-t border-neutral-800">
        <div className="section-container">
          <h2 className="text-center text-2xl md:text-3xl font-bold text-white mb-6">
            Popular Packages
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                name: "Basic Package",
                price: "$200",
                description: "Perfect for smaller properties",
                features: [
                  "Up to 25 professional photos",
                  "Interior and exterior shots",
                  "HDR processing",
                  "48-72 hour delivery",
                  "Online gallery upon request",
                ],
                popular: false,
              },
              {
                name: "Professional Package",
                price: "$350",
                description: "Most popular for standard homes",
                features: [
                  "Up to 35 professional photos",
                  "Aerial drone photography (5 shots)",
                  "Twilight exterior shot",
                  "HDR processing",
                  "48-72 hour delivery",
                  "Online gallery upon request",
                ],
                popular: true,
              },
              {
                name: "Premium Package",
                price: "$600",
                description: "Complete coverage for luxury properties",
                features: [
                  "Up to 50 professional photos",
                  "Aerial drone photography (10 shots)",
                  "Cinematic video tour (2-3 min)",
                  "Twilight photography",
                  "HDR processing",
                  "48 hour delivery",
                  "3d floor plan",
                  "Online gallery",
                ],
                popular: false,
              },
            ].map((pkg) => (
              <div
                key={pkg.name}
                className={`rounded-xl p-8 ${
                  pkg.popular ? "bg-primary-600 transform scale-105 shadow-2xl" : "bg-neutral-800"
                }`}
              >
                {pkg.popular && (
                  <div className="bg-accent-400/20 text-accent-400 border border-accent-400/40 backdrop-blur-sm text-sm font-bold uppercase px-3 py-1 rounded-full inline-block mb-4">
                    Most Popular
                  </div>
                )}
                <h3 className="text-2xl font-bold mb-2 text-white">{pkg.name}</h3>
                <p className="text-neutral-300 mb-4">{pkg.description}</p>
                <p className="text-4xl font-bold mb-6 text-white">{pkg.price}</p>
                <ul className="space-y-3 mb-8">
                  {pkg.features.map((feature) => (
                    <li key={feature} className="flex items-start text-sm text-neutral-300">
                      <svg
                        className="w-5 h-5 text-primary-600 mr-2 flex-shrink-0 mt-0.5"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                          clipRule="evenodd"
                        />
                      </svg>
                      {feature}
                    </li>
                  ))}
                </ul>
                <Link
                  to={ROUTES.BOOKING}
                  className={`block w-full text-center py-3 rounded-lg font-medium transition-colors ${
                    pkg.popular
                      ? "bg-white text-primary-600 hover:bg-neutral-100"
                      : "bg-primary-600 text-white hover:bg-primary-700"
                  }`}
                >
                  Select Package
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-8 md:py-12 bg-white dark:bg-neutral-950 border-t border-neutral-200 dark:border-neutral-800">
        <div className="section-container">
          <h2 className="text-4xl md:text-5xl font-bold text-neutral-900 dark:text-white text-center mb-8">
            Why Choose WIH Media
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center group">
              <div className="w-24 h-24 bg-gradient-to-br from-primary-500 to-primary-600 rounded-3xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300 shadow-xl">
                <span className="text-4xl">⚡</span>
              </div>
              <h3 className="text-2xl font-bold mb-3 text-neutral-900 dark:text-white">
                Fast Turnaround
              </h3>
              <p className="text-neutral-600 dark:text-neutral-300 leading-relaxed">
                Get your photos and videos delivered quickly without compromising quality. Most
                packages ready in 24-48 hours.
              </p>
            </div>
            <div className="text-center group">
              <div className="w-24 h-24 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-3xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300 shadow-xl">
                <span className="text-4xl">🎨</span>
              </div>
              <h3 className="text-2xl font-bold mb-3 text-neutral-900 dark:text-white">
                Professional Quality
              </h3>
              <p className="text-neutral-600 dark:text-neutral-300 leading-relaxed">
                State-of-the-art equipment and expert editing for stunning results that make
                listings stand out.
              </p>
            </div>
            <div className="text-center group">
              <div className="w-24 h-24 bg-gradient-to-br from-purple-500 to-purple-600 rounded-3xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300 shadow-xl">
                <span className="text-4xl">💰</span>
              </div>
              <h3 className="text-2xl font-bold mb-3 text-neutral-900 dark:text-white">
                Competitive Pricing
              </h3>
              <p className="text-neutral-600 dark:text-neutral-300 leading-relaxed">
                Flexible packages and pricing options to fit your budget. Simple, transparent
                pricing for every listing.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-16 bg-neutral-50 dark:bg-neutral-900 border-t border-neutral-200 dark:border-neutral-800">
        <div className="section-container">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-neutral-900 dark:text-white mb-4">
              Simple process, consistent results
            </h2>
            <p className="text-xl text-neutral-600 dark:text-neutral-300">
              From booking to delivery, everything is designed to keep your listing timeline moving.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                title: "Book",
                body: "Pick a date and select the services you need for the property.",
                icon: "📅",
              },
              {
                title: "Shoot",
                body: "We capture interiors, exteriors, and add-ons with a clean, modern style.",
                icon: "📸",
              },
              {
                title: "Edit",
                body: "Professional post-production for natural color and crisp details.",
                icon: "🛠️",
              },
              {
                title: "Deliver",
                body: "Receive your final media ready for MLS, web, and social.",
                icon: "📦",
              },
            ].map((step) => (
              <Card key={step.title}>
                <CardBody>
                  <div className="w-12 h-12 rounded-xl bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
                    <span className="text-2xl">{step.icon}</span>
                  </div>
                  <h3 className="mt-4 text-lg font-semibold text-neutral-900 dark:text-white">
                    {step.title}
                  </h3>
                  <p className="mt-2 text-neutral-600 dark:text-neutral-300">{step.body}</p>
                </CardBody>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Software Engineering & App Creation Consulting (combined) */}
      <section className="py-16 bg-white dark:bg-neutral-950 border-t border-neutral-200 dark:border-neutral-800">
        <div className="section-container">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-red-500/10 border border-red-500/30 text-red-600 dark:text-red-400 px-4 py-2 rounded-full text-sm font-bold uppercase tracking-wider mb-4">
              <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
              Beyond Real Estate Media
            </div>
            <h2 className="text-4xl font-bold text-neutral-900 dark:text-white mb-4">
              Software Engineering & App Creation Consulting
            </h2>
            <p className="text-xl text-neutral-600 dark:text-neutral-300 max-w-2xl mx-auto">
              From prototyping to production — architecture, integrations, automation, reliability,
              and website design.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                title: "Architecture & Roadmapping",
                body: "Plan scalable systems, choose the right stack, and de‑risk delivery.",
                icon: "🧭",
              },
              {
                title: "App Development & Integrations",
                body: "Build web apps, APIs, and connect third‑party services.",
                icon: "🔌",
              },
              {
                title: "Automation & AI Workflows",
                body: "Streamline processes with automation and AI‑assisted tools.",
                icon: "🤖",
              },
              {
                title: "Performance & Reliability",
                body: "Optimize speed, uptime, and quality with testing and observability.",
                icon: "⚙️",
              },
            ].map((item) => (
              <Card key={item.title} hover className="group">
                <CardBody>
                  <div className="w-12 h-12 rounded-xl bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center transition-transform group-hover:scale-110 group-hover:rotate-3">
                    <span className="text-2xl">{item.icon}</span>
                  </div>
                  <h3 className="mt-4 text-lg font-semibold text-neutral-900 dark:text-white">
                    {item.title}
                  </h3>
                  <p className="mt-2 text-neutral-600 dark:text-neutral-300">{item.body}</p>
                </CardBody>
              </Card>
            ))}
          </div>

          {/* Removed bottom Order Status Tracking as requested */}

          <div className="mt-10 flex justify-center">
            <Link to={ROUTES.CONTACT}>
              <Button variant="primary" size="lg">
                Contact Us About Your Project
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Reviews Section */}
      <ReviewsSection />

      {/* FAQ moved to dedicated page */}

      {/* Service Area Section */}
      <section className="py-16 bg-neutral-50 dark:bg-neutral-900 border-t border-neutral-200 dark:border-neutral-800">
        <div className="section-container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div>
              <h2 className="text-4xl font-bold text-neutral-900 dark:text-white">
                Proudly serving Indian River County
              </h2>
              <p className="mt-4 text-lg text-neutral-600 dark:text-neutral-300">
                Professional real estate photography, drone services, video walkthroughs, Matterport
                3D tours, floor plans, virtual twilight, vertical reels, and 360 tours for agents,
                brokers, and property managers across Indian River County.
              </p>
              <div className="mt-8 flex flex-wrap gap-4">
                <Link to={ROUTES.BOOKING}>
                  <Button variant="primary" size="lg">
                    Check Availability
                  </Button>
                </Link>
                <Link to={ROUTES.CONTACT}>
                  <Button variant="outline" size="lg">
                    Request a Quote
                  </Button>
                </Link>
              </div>
            </div>

            <div className="rounded-2xl border border-neutral-200 bg-white p-6 dark:border-neutral-800 dark:bg-neutral-900">
              <p className="text-sm uppercase tracking-[0.2em] text-primary-700 dark:text-primary-300">
                Built for real estate teams
              </p>
              <h3 className="mt-3 text-2xl font-semibold text-neutral-900 dark:text-white">
                Consistent media for every listing
              </h3>
              <ul className="mt-4 space-y-3 text-neutral-700 dark:text-neutral-200">
                <li>
                  <span className="font-semibold text-neutral-900 dark:text-white">
                    Agent-friendly scheduling:
                  </span>
                  <span className="ml-1">Quick booking and clear confirmations.</span>
                </li>
                <li>
                  <span className="font-semibold text-neutral-900 dark:text-white">
                    Clean, modern edits:
                  </span>
                  <span className="ml-1">Natural color and balanced lighting.</span>
                </li>
                <li>
                  <span className="font-semibold text-neutral-900 dark:text-white">
                    Marketing-ready deliverables:
                  </span>
                  <span className="ml-1">Optimized for common platforms and use cases.</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-to-r from-primary-700 via-primary-600 to-neutral-900 text-white py-16">
        <div className="section-container text-center">
          <h2 className="text-4xl font-bold mb-4">Ready to Get Started?</h2>
          <p className="text-xl text-white/90 mb-8">
            Book your photography session today and make your properties stand out
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link
              to={ROUTES.BOOKING}
              className="inline-block bg-white text-primary-600 px-8 py-3 rounded-lg hover:bg-neutral-100 transition-colors font-medium"
            >
              Book Now
            </Link>
            <Link
              to={ROUTES.CONTACT}
              className="inline-block bg-primary-700 text-white px-8 py-3 rounded-lg hover:bg-primary-800 transition-colors font-medium border border-primary-500"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
